import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MatrixClient, Room } from 'matrix-js-sdk';
import { Message, SearchData } from '../../pages/NewChat/types';
import { NewChatState } from 'data/types';

const initialState: NewChatState = {
  loading: true,
  client: null,
  selectedRoom: null,
  searchQuery: '',
  searchResults: [],
  dmRooms: [],
  publicRooms: [],
  invitations: [],
  messages: {},
  alert: {
    open: false,
    severity: 'success',
    message: ''
  }
};

const newChat = createSlice({
  name: 'newChat',
  initialState,
  reducers: {
    clearChatState(state) {
      Object.assign(state, initialState);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setClient(state, action: PayloadAction<MatrixClient>) {
      state.client = action.payload;
    },
    setSelectedRoom(state, action: PayloadAction<Room | null>) {
      state.selectedRoom = action.payload;
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    setSearchResults(state, action: PayloadAction<SearchData[]>) {
      state.searchResults = action.payload;
    },
    setDmRooms(state, action: PayloadAction<Room[]>) {
      state.dmRooms = action.payload;
    },
    setPublicRooms(state, action: PayloadAction<Room[]>) {
      state.publicRooms = action.payload;
    },
    setRoomMessages(state, action: PayloadAction<{ roomId: string; messages: Message[] }>) {
      state.messages[action.payload.roomId] = action.payload.messages;
    },
    updateReadReceipt: (state, action: PayloadAction<{ roomId: string; eventId: string }>) => {
      const { roomId, eventId } = action.payload;
      const messages = state.messages[roomId];
      if (!messages) return;

      const msg = messages.find((m) => m.eventId === eventId);
      if (msg) {
        msg.status = 'read';
      }
    },
    addMessage(state, action: PayloadAction<{ roomId: string; message: Message }>) {
      const { roomId, message } = action.payload;
      if (!state.messages[roomId]) {
        state.messages[roomId] = [];
      }
      state.messages[roomId].push(message);
    },
    addOlderMessages(state, action: PayloadAction<{ roomId: string; messages: Message[] }>) {
      const { roomId, messages } = action.payload;
      state.messages[roomId] = messages;
    },
    setInvitations: (state, action: PayloadAction<Room[]>) => {
      state.invitations = action.payload;
    },
    clearMessages(state) {
      state.messages = {}; // Resets all messages
    }
  }
});

export const {
  clearChatState,
  setLoading,
  setClient,
  setSelectedRoom,
  setSearchQuery,
  setSearchResults,
  updateReadReceipt,
  setInvitations,
  setDmRooms,
  setPublicRooms,
  setRoomMessages,
  addMessage,
  addOlderMessages,
  clearMessages
} = newChat.actions;

export default newChat.reducer;
